import React from "react";
import { Box, Typography } from "@mui/material";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import { ITemplatePlaceholderValues } from "../../../../../types/campaign.types";
import { IPlaceholderFetchStatusDetails } from "./MiddleSection";
import { getErrorMsg } from "../../../../../utils/whatsAppUtils";
import MessageLoader from "./MessageLoader";

interface IProps {
  message: string;
  placeholders: ITemplatePlaceholderValues;
  placeholderFetchStatusDetails: IPlaceholderFetchStatusDetails;
}

export const renderMsg = ({
  content,
  isPreview,
  placeholders,
  handleShowFullMsg,
}: {
  content: string;
  isPreview?: boolean;
  placeholders: ITemplatePlaceholderValues;
  handleShowFullMsg?: () => void;
}) => {
  const replacedContent = isPreview
    ? content.replace(/@(\w+)/g, (match, key) => {
        return placeholders[`@${key}`] || match;
      })
    : content;

  // Split content for rendering and handle "Read more"
  const lines = replacedContent.split("<br>");
  const lastLine = lines.pop(); // Get the last line

  return (
    <Typography variant="h6" sx={{ ...(!isPreview && { lineHeight: "24px" }) }}>
      {/* Render all lines except the last one */}
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line
            .split(/(\s+|(?=@\w+)|(?<=@\w+)(?=[.,-;!?]))/)
            .map((part, partIndex) => {
              if (part.startsWith("@") && /^\@\w+$/.test(part) && !isPreview) {
                return (
                  <Box
                    key={partIndex}
                    component="span"
                    sx={{
                      border: "1px solid green",
                      borderRadius: "4px",
                      px: 0.25,
                      margin: "0 2px",
                      display: "inline",
                    }}
                  >
                    {part}
                  </Box>
                );
              }
              // Return the plain text
              return part;
            })}
          <br />
        </React.Fragment>
      ))}

      {/* Render the last line and add "Read more" */}
      {lastLine
        ?.split(/(\s+|(?=@\w+)|(?<=@\w+)(?=[.,;!?]))/)
        .map((part, partIndex) => {
          if (part.startsWith("@") && /^\@\w+$/.test(part) && !isPreview) {
            return (
              <Box
                key={partIndex}
                component="span"
                sx={{
                  border: "1px solid green",
                  borderRadius: "4px",
                  px: 0.25,
                  margin: "0 2px",
                  display: "inline",
                }}
              >
                {part}
              </Box>
            );
          }
          // Return plain text
          return part;
        })}

      {/* Show "Read more" only if isPreview is true */}
      {isPreview && handleShowFullMsg && (
        <Typography
          component="span"
          variant="h6"
          sx={{
            color: COLOR_MAP.WHATS_APP_GREEN,
            cursor: "pointer",
            fontWeight: "fontWeightBold",
            whiteSpace: "nowrap",
          }}
          onClick={handleShowFullMsg}
        >
          {" "}
          Read more
        </Typography>
      )}
    </Typography>
  );
};

const Message: React.FC<IProps> = ({
  message,
  placeholders,
  placeholderFetchStatusDetails,
}) => {
  
  return (
    <Box
      sx={{
        backgroundColor: COLOR_MAP.WHITE,
        px: 2,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2,
          backgroundColor: COLOR_MAP.GREY_9,
          borderRadius: "8px",
          flexGrow: 1,
        }}
      >
        {placeholderFetchStatusDetails?.status === "pending" && (
          <MessageLoader />
        )}
        {placeholderFetchStatusDetails?.status === "fulfilled" && (
          <Typography variant="h6">
            {renderMsg({ content: message, placeholders })}
          </Typography>
        )}

        {placeholderFetchStatusDetails?.status === "rejected" && (
          <Typography color="error" variant="h6">
            {getErrorMsg({
              error: placeholderFetchStatusDetails?.error,
              defaultMsg:
                "Failed to load the template content. Please try again.",
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Message;
